import { useEffect, useRef } from "react";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import { toast } from "sonner";

const PING_INTERVAL_MS = 60_000; // every 60s while signed in
const STORAGE_KEY = "fa_loc_consent";

/**
 * Mounts once at the app root. When a staff member (tech/admin/owner) is signed in,
 * asks for browser geolocation permission once, then periodically POSTs their position
 * so the Map page can plot live staff. Customers are not tracked.
 */
export default function LocationTracker() {
  const { user } = useAuth();
  const watchIdRef = useRef(null);
  const pingTimerRef = useRef(null);
  const lastSentRef = useRef(0);
  const isStaff = user && ["technician", "admin", "owner"].includes(user.role);

  useEffect(() => {
    if (!isStaff || !navigator.geolocation) return undefined;

    let consent = null;
    try { consent = localStorage.getItem(STORAGE_KEY); } catch { /* ignore */ }

    const send = (pos) => {
      if (!pos?.coords) return;
      const now = Date.now();
      // Throttle to one POST per 30s minimum
      if (now - lastSentRef.current < 30_000) return;
      lastSentRef.current = now;
      api
        .patch("/me/location", {
          lat: pos.coords.latitude,
          lng: pos.coords.longitude,
          accuracy: pos.coords.accuracy,
        })
        .catch((err) => {
          if (err?.response?.status && err.response.status !== 401) {
            console.warn("location ping failed:", err.message);
          }
        });
    };

    const tickOnce = () => {
      navigator.geolocation.getCurrentPosition(
        (pos) => send(pos),
        (err) => {
          if (err.code === 1 /* PERMISSION_DENIED */) {
            try { localStorage.setItem(STORAGE_KEY, "denied"); } catch { /* ignore */ }
          }
        },
        { enableHighAccuracy: false, timeout: 8000, maximumAge: 60_000 },
      );
    };

    const start = () => {
      tickOnce();
      pingTimerRef.current = setInterval(tickOnce, PING_INTERVAL_MS);
    };

    if (consent === "denied") {
      // Don't pester — user explicitly declined earlier
      return undefined;
    }

    if (consent === "granted") {
      start();
    } else {
      // Ask once: a single getCurrentPosition triggers the browser prompt
      navigator.geolocation.getCurrentPosition(
        (pos) => {
          try { localStorage.setItem(STORAGE_KEY, "granted"); } catch { /* ignore */ }
          send(pos);
          pingTimerRef.current = setInterval(tickOnce, PING_INTERVAL_MS);
        },
        (err) => {
          if (err.code === 1) {
            try { localStorage.setItem(STORAGE_KEY, "denied"); } catch { /* ignore */ }
            toast.message("Location sharing off — you can enable it in your browser to appear on the team map.");
          }
        },
        { enableHighAccuracy: false, timeout: 10000 },
      );
    }

    return () => {
      if (pingTimerRef.current) { clearInterval(pingTimerRef.current); pingTimerRef.current = null; }
      if (watchIdRef.current != null) { navigator.geolocation.clearWatch(watchIdRef.current); watchIdRef.current = null; }
    };
  }, [isStaff]);

  return null;
}
